import { CustomTooltip, IconButton } from '@/shared/ui/components';
import { CONFLICT_USER_COLORS } from '@/shared/config';
import { generateFullName } from '@/shared/lib';
import classNames from 'classnames';
import _ from 'lodash';
import { Fragment } from 'react';
import { useHeaderRightButtons } from './useHeaderRightButtons';
import type { HeaderRightButtonsProps } from './layout.types';

const EMPTY_ARRAY: any[] = [];

const HeaderRightButtons = ({
  className,
  data,
  conflictUsers = EMPTY_ARRAY,
  isReportsPage = false,
  limitUserCount = 0,
}: HeaderRightButtonsProps) => {
  const { listButton } = useHeaderRightButtons({ data, conflictUsers });

  /**
   * Renders an avatar badge for another user editing the same event. Builds initials from the first and last name, picks a background colour by indexing CONFLICT_USER_COLORS with the doubled first character code, and adds a full-name tooltip everywhere except the reports page.
   *
   * @param conflictUser - Conflicting user with first name, last name and token.
   * @returns The conflict user avatar element.
   */
  const generateConflicUserIcon = (conflictUser: any) => {
    const { firstName, lastName, token } = conflictUser;
    const firstCharacterOfFirstName = firstName.charAt(0);
    const firstCharacterOfLastName = lastName.charAt(0);
    const charactersInHex = firstName.charCodeAt(0) + firstName.charCodeAt(0);
    const backgroundColor = CONFLICT_USER_COLORS[charactersInHex % CONFLICT_USER_COLORS.length];
    return (
      <CustomTooltip title={!isReportsPage ? generateFullName(conflictUser) : undefined}>
        <div
          id={`conflict-user-icon-${token}`}
          className="conflict-user-icon"
          style={{ backgroundColor }}
        >
          <span className="name">{`${firstCharacterOfFirstName}${firstCharacterOfLastName}`}</span>
        </div>
      </CustomTooltip>
    );
  };

  return (
    <div className={classNames('header-right-buttons', className)}>
      <div className="avatar flex-row__ic">
        {conflictUsers.length <= limitUserCount ? (
          _.map(conflictUsers, (conflictUser) => generateConflicUserIcon(conflictUser))
        ) : (
          <>
            {_.map(conflictUsers.slice(0, limitUserCount - 1), (conflictUser) =>
              generateConflicUserIcon(conflictUser),
            )}

            <div className="conflict-user-icon" style={{ backgroundColor: '#C6CBD1' }}>
              <span className="name">{`+${conflictUsers.length - (limitUserCount - 1)}`}</span>
            </div>
          </>
        )}
      </div>
      {_.map(listButton, (icon, index) => (
        <Fragment key={index}>
          <CustomTooltip title={icon.name}>
            <div>
              <IconButton
                id={icon.id}
                className={classNames(
                  index > 0 ? 'ml3' : '',
                  icon.isConflictButton ? '--conflict' : '',
                  icon.isCanNotEdit ? '--disabled' : '',
                )}
                isFilledGradient
                disabled={icon.disabled}
                iconComponent={<img src={icon.image} alt={icon.imageAlternate} />}
                onClick={icon.isCanNotEdit ? undefined : icon.handleClick}
                isIconLinkButton={icon.isIconLinkButton}
                link={icon.isCanNotEdit ? '#' : icon.link}
                isLoading={icon.isLoading}
                aria-label={icon.name}
              />
            </div>
          </CustomTooltip>
        </Fragment>
      ))}
    </div>
  );
};

export default HeaderRightButtons;
